import { t } from 'i18next';
import React, { useState } from 'react';
import PlaidLink from '~src/domains/common/components/PlaidLink';
import { PageLoadingIndicator } from 'up-ops-web-shared-components';
import Alerter from '~src/domains/common/components/Alerter';
import InputGroup from '~src/domains/common/components/InputGroup';
import { Page } from '~src/domains/common/enums/api';
import {
  AlertNames,
  AcceptFormNames,
  PaymentOption,
} from '~src/domains/common/enums/common';
import AutoPayCheckbox from './AutoPayCheckbox';
import AutoPayModal from './AutoPayModal';
import CardForm from './CardForm';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as commonSelectors from '~src/domains/common/state/commonSelectors';
import { AcceptOption } from '../enums/accept';
import * as styles from '~src/styles/domains/accept/AcceptPage.module.scss';
import {
  acceptFormSelector,
  paymentSelectorErrorSelector,
} from '../state/acceptSelectors';
import {
  general,
  setPaymentMethod,
} from '~src/domains/common/state/commonReducer';
import { trackClick, trackFormBlur } from '~src/utils/tracking';
import * as acceptFormActions from '~src/domains/accept/state/acceptFormReducer';
import { MastercardIcon, VisaIcon } from '~src/domains/common/assets/svg';
import { xhrPlaidLinkToken } from '~src/utils/xhr';
import { setPaymentSelectorError } from '../state/acceptReducer';
import { Locale } from '~src/domains/common/enums/localization';
import { useTranslation } from 'react-i18next';
import SplitOfferSelected from './SplitOfferSelected';
import ChangePaymentPlan from './ChangePaymentPlan';

interface PaymentMethodsProps {}

export default function PaymentMethods({}: PaymentMethodsProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const page = useSelector(commonSelectors.pageSelector);
  const [autoPayModalVisible, setAutoPayModalVisible] =
    useState<boolean>(false);
  const options = useSelector(commonSelectors.optionsSelector);
  const showCard = options.includes(AcceptOption.CARD);
  const mcode = useSelector(commonSelectors.mcodeSelector);
  const selectedPaymentMethod = useSelector(
    commonSelectors.paymentMethodSelector,
  );
  const plaidLinkToken = useSelector(commonSelectors.plaidLinkTokenSelector);
  const bankMetadata = useSelector(commonSelectors.bankMetadataSelector);
  const paymentSelectorError = useSelector(paymentSelectorErrorSelector);
  const plaidDisabled = useSelector(commonSelectors.plaidDisabledSelector);
  const paymentOptions = useSelector(commonSelectors.paymentOptionsSelector);
  const acceptForm = useSelector(acceptFormSelector);
  const locale = useSelector(commonSelectors.localeSelector);

  const inputs = t('accept:inputs', {
    returnObjects: true,
  }) as acceptFormActions.AcceptLocalization;

  const bankAccountSelected =
    selectedPaymentMethod === PaymentOption.BANK_ACCOUNT;
  const isPlaidFlowCompleted = bankAccountSelected && !!bankMetadata;

  /** Takes an array of payment options and generates selector buttons for them. */
  const paymentOptionsView = paymentOptions.map(paymentOption => {
    const isSelected = paymentOption === selectedPaymentMethod;
    const isBankAccount = paymentOption === PaymentOption.BANK_ACCOUNT;
    const isDisabled = isBankAccount && plaidDisabled;
    const icon = isBankAccount ? (
      <span className={styles['accept-recommended']}>
        {t('accept:recommended')}
      </span>
    ) : (
      <span className={styles['accept-card-icons']}>
        {<MastercardIcon />}
        {<VisaIcon />}
      </span>
    );
    const className =
      isSelected && !isDisabled
        ? `${styles['accept-payment-option-button']} selected`
        : styles['accept-payment-option-button'];
    return (
      <button
        id={paymentOption}
        key={paymentOption}
        onClick={() => {
          if (!isSelected) {
            dispatch(setPaymentMethod(paymentOption));
          }
          if (paymentSelectorError) {
            dispatch(setPaymentSelectorError(null));
          }
          if (
            !isSelected &&
            isBankAccount &&
            !plaidLinkToken &&
            !bankMetadata
          ) {
            dispatch(xhrPlaidLinkToken(locale));
          }
        }}
        className={className}
        disabled={isDisabled}
      >
        <div className={styles['accept-payment-option']}>
          <span className={styles['accept-payment-option-text']}>
            {t(`accept:payment-option.${paymentOption}`)}
          </span>
          {icon}
        </div>
        {paymentOption === PaymentOption.BANK_ACCOUNT && (
          <div className={styles['accept-payment-option-desc']}>
            {t(`accept:payment-option.${paymentOption}-desc`)}
          </div>
        )}
      </button>
    );
  });

  const brand = useSelector(commonSelectors.brandSelector);

  const showCardInputs =
    showCard && !bankAccountSelected && !!selectedPaymentMethod;
  const isPlaidLinkTokenLoading =
    bankAccountSelected && !plaidLinkToken && !bankMetadata;

  const showSsnField =
    inputs[AcceptFormNames.FULL_SSN] && locale === Locale.enUS;

  const onAutoPayChange = (value: boolean) => {
    if (value === false) {
      setAutoPayModalVisible(true);
    }

    batch(() => {
      dispatch(acceptFormActions.updateInputValue({ name: 'autopay', value }));
      dispatch(trackClick('autopay', { value }));
    });
  };

  const onAutoPayBlur = () => {
    dispatch(trackFormBlur('autopay'));
  };

  const openPaymentFaqHandler = () => {
    dispatch(general({ page: Page.FAQ, previousPage: page }));
  };

  return (
    <>
      <ChangePaymentPlan />
      <SplitOfferSelected />
      {mcode != undefined && (
        <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
      )}
      <div className={styles['accept-terms']}>
        {t(`accept:${brand.toLowerCase()}.terms`)}
      </div>
      <div className={styles['accept-input-header-wrapper']}>
        <h3 className={`header-txt ${styles['accept-input-header']}`}>
          {t('accept:inputs-header')}
        </h3>
      </div>
      <button
        type='button'
        className={styles['accept-payment-faq-button']}
        aria-label='open payment FAQ button'
        aria-describedby='payment-faq-button'
        onClick={() => dispatch(openPaymentFaqHandler)}
      >
        {t(`accept:payment-faq-button`)}
      </button>
      {paymentSelectorError && (
        <div className={styles['accept-payment-method-error-wrapper']}>
          <Alerter type={AlertNames.ERROR} i18nKey={paymentSelectorError} />
        </div>
      )}
      <div className={styles['accept-payment-options-wrapper']}>
        <div className={styles['accept-select-payment-method-header']}>
          {t('accept:select-payment-method')}
        </div>
        {paymentOptionsView}
      </div>
      {isPlaidLinkTokenLoading && (
        <div className={styles['accept-plaid-loading-indicator']}>
          <PageLoadingIndicator message='' />
        </div>
      )}
      {bankAccountSelected && !bankMetadata && plaidLinkToken && <PlaidLink />}
      {!!bankMetadata && (
        <div
          className={
            bankAccountSelected
              ? styles['accept-bank-account-details-wrapper']
              : `${styles['accept-bank-account-details-wrapper-hidden']}` // This is here to prevent re-rendering when toggling the payment selector.
          }
        >
          <div className={styles['accept-bank-account-details']}>
            <span>{bankMetadata.institution}</span>
            <div>{`****${bankMetadata.account.mask}`}</div>
          </div>
          <div className={styles['accept-bank-account-edit']}>
            <PlaidLink label={t('accept:edit')} />
          </div>
        </div>
      )}
      {isPlaidFlowCompleted && showSsnField && (
        <div className={styles['accept-plaid-ssn-input']}>
          <InputGroup
            state={acceptForm[AcceptFormNames.FULL_SSN]}
            input={inputs[AcceptFormNames.FULL_SSN]}
            id={AcceptFormNames.FULL_SSN}
            page={Page.ACCEPT}
            reduxActions={acceptFormActions}
          />
        </div>
      )}
      <CardForm inputs={inputs} visible={showCardInputs} />
      <AutoPayCheckbox
        id={AcceptFormNames.AUTOPAY}
        checked={
          (acceptForm?.[AcceptFormNames.AUTOPAY]?.value as boolean) ?? true
        }
        onChangePost={onAutoPayChange}
        onBlurHandler={onAutoPayBlur}
      />

      <AutoPayModal
        isAutoPayModalVisible={autoPayModalVisible}
        setAutoPayModalVisible={setAutoPayModalVisible}
        updateInputValue={acceptFormActions.updateInputValue}
      />
    </>
  );
}
